import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import config from '../../config/config';
import DataTable from 'react-data-table-component';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import DashboardLayout from '../shared/layout/DashboardLayout';
import { FaEye, FaCheckCircle, FaTimesCircle, FaSyncAlt } from 'react-icons/fa';
import { Tooltip } from 'react-tooltip';
import Modal from '../../components/common/Modal';
import { getPublikatorMenuItems } from '../../utils/menuItems';
import '../../styles/App.css';
import '../../styles/PublikatorDataPublication.css';

const PublikatorDataPage = () => {
  const tableRef = useRef(null);
  const [dataList, setDataList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [username, setUsername] = useState('');
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterTitle, setFilterTitle] = useState('Semua Data');
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [publishAction, setPublishAction] = useState(null); // 'publish' or 'unpublish'
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [stats, setStats] = useState({
    totalData: 0,
    dataPublished: 0,
    dataPending: 0,
    dataBelumPublikasi: 0,
    dataSiapPublikasi: 0,
    dataSudahPublikasi: 0
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const decoded = jwtDecode(token);
      
      if (!token || !decoded.twoFactorVerified) {
        navigate('/');
        return;
      }

      if (decoded.role !== 3) {
        navigate('/');
        return;
      }

      setUsername(decoded.username);

      // Fetch data yang sudah terintegrasi (untuk dipublikasi)
      const filterParam = searchParams.get('filter');
      const filterQuery = filterParam ? `?filter=${filterParam}` : '';
      const response = await axios.get(`${config.API_URL}/publikator/data${filterQuery}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const sortedData = response.data.sort((a, b) => 
        new Date(b.last_modified) - new Date(a.last_modified)
      );

      setDataList(sortedData);
      setFilteredData(sortedData);

      // Calculate stats - fetch stats from API
      const statsResponse = await axios.get(`${config.API_URL}/publikator/stats`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      // Fetch all data for detailed stats
      const allDataResponse = await axios.get(`${config.API_URL}/publikator/data`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const allData = allDataResponse.data;

      const belumPublikasi = allData.filter(item => 
        !item.status_publikasi || item.status_publikasi === 'Belum Publikasi'
      ).length;
      const siapPublikasi = allData.filter(item => 
        item.status_publikasi === 'Siap Publikasi'
      ).length;
      const sudahPublikasi = allData.filter(item => 
        item.status_publikasi === 'Sudah Publikasi'
      ).length;

      setStats({
        totalData: statsResponse.data.totalData || allData.length,
        dataPublished: statsResponse.data.dataPublished || sudahPublikasi,
        dataPending: statsResponse.data.dataPending || (belumPublikasi + siapPublikasi),
        dataBelumPublikasi: belumPublikasi,
        dataSiapPublikasi: siapPublikasi,
        dataSudahPublikasi: sudahPublikasi
      });

      // Apply filter from URL params
      if (filterParam === 'belum') {
        handleStatCardClick('belum');
      } else if (filterParam === 'siap') {
        handleStatCardClick('siap');
      } else if (filterParam === 'sudah') {
        handleStatCardClick('sudah');
      }

    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Tidak Ada Data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [navigate]);

  const handleSearch = async () => {
    setSearchLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Build query params
      const params = new URLSearchParams();
      if (activeFilter) {
        params.append('filter', activeFilter);
      }
      if (searchText.trim()) {
        params.append('search', searchText.trim());
      }
      
      const queryString = params.toString();
      const url = `${config.API_URL}/publikator/data${queryString ? `?${queryString}` : ''}`;
      
      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const sortedData = response.data.sort((a, b) => 
        new Date(b.last_modified) - new Date(a.last_modified)
      );

      setFilteredData(sortedData);
    } catch (err) {
      console.error('Error searching data:', err);
      setFilteredData([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Clear search and reset to current filter
  const handleClearSearch = async () => {
    setSearchText('');
    const token = localStorage.getItem('token');
    
    const params = new URLSearchParams();
    if (activeFilter) {
      params.append('filter', activeFilter);
    }
    
    const queryString = params.toString();
    const url = `${config.API_URL}/publikator/data${queryString ? `?${queryString}` : ''}`;
    
    try {
      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const sortedData = response.data.sort((a, b) => 
        new Date(b.last_modified) - new Date(a.last_modified)
      );
      setFilteredData(sortedData);
    } catch (err) {
      console.error('Error clearing search:', err);
    }
  };

  // Refresh data and stats
  const handleRefresh = async () => {
    setSearchText('');
    setActiveFilter(null);
    setFilterTitle('Semua Data');
    await fetchData();
  };

  const handleStatCardClick = async (type) => {
    const token = localStorage.getItem('token');
    
    // Build query params with search text
    const params = new URLSearchParams();
    if (type !== 'all') {
      params.append('filter', type);
    }
    if (searchText.trim()) {
      params.append('search', searchText.trim());
    }
    
    const queryString = params.toString();
    const url = `${config.API_URL}/publikator/data${queryString ? `?${queryString}` : ''}`;
    
    // Set filter state and title
    if (type === 'all') {
      setActiveFilter(null);
      setFilterTitle('Semua Data');
    } else if (type === 'belum') {
      setActiveFilter('belum');
      setFilterTitle('Data Belum Publikasi');
    } else if (type === 'siap') {
      setActiveFilter('siap');
      setFilterTitle('Data Siap Publikasi');
    } else if (type === 'sudah') {
      setActiveFilter('sudah');
      setFilterTitle('Data Sudah Dipublikasi');
    }
    
    try {
      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const sortedData = response.data.sort((a, b) => 
        new Date(b.last_modified) - new Date(a.last_modified)
      );
      setFilteredData(sortedData);
    } catch (err) {
      console.error('Error fetching filtered data:', err);
      setFilteredData([]);
    }

    setTimeout(() => {
      if (tableRef.current) {
        tableRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 100);
  };

  const handlePublishClick = (noTiket, currentStatus) => {
    setSelectedTicket(noTiket);
    setPublishAction(currentStatus ? 'unpublish' : 'publish');
    setShowPublishModal(true);
  };

  const handleConfirmPublish = async () => {
    if (!selectedTicket) return;

    try {
      const token = localStorage.getItem('token');
      const newStatus = publishAction === 'publish';

      await axios.put(
        `${config.API_URL}/publikator/toggle-publish/${selectedTicket}`,
        { publish: newStatus },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setShowPublishModal(false);
      const statusText = newStatus ? 'dipublikasi' : 'dibatalkan publikasinya';
      setSuccessMessage(`Data dengan tiket ${selectedTicket} berhasil ${statusText}.`);
      setShowSuccessModal(true);
      
      await fetchData();
    } catch (error) {
      console.error('Error toggling publish status:', error);
      setShowPublishModal(false);
      const errorMsg = error.response?.data?.message || 'Gagal mengubah status publikasi data.';
      alert(errorMsg);
    }
  };

  const columns = [
    {
      name: 'No',
      selector: (row, index) => index + 1,
      sortable: false,
      width: '60px',
      cell: (row, index) => <div style={{ fontWeight: '500' }}>{index + 1}</div>
    },
    {
      name: 'No Tiket',
      selector: row => row.no_tiket,
      sortable: true,
      minWidth: '120px',
    },
    {
      name: 'Nama IGT',
      selector: row => row.nama_igt,
      sortable: true,
      minWidth: '200px',
      cell: row => (
        <div data-tooltip-id="nama-tooltip" data-tooltip-content={row.nama_igt}>
          {row.nama_igt.length > 50 ? `${row.nama_igt.substring(0, 50)}...` : row.nama_igt}
        </div>
      )
    },
    {
      name: 'KL',
      selector: row => row.kl,
      sortable: true,
      minWidth: '120px',
      cell: row => (
        <div data-tooltip-id="kl-tooltip" data-tooltip-content={row.kl}>
          {row.kl.length > 30 ? `${row.kl.substring(0, 30)}...` : row.kl}
        </div>
      )
    },
    {
      name: 'Status',
      selector: row => row.status_publikasi || '-',
      sortable: true,
      minWidth: '130px',
      cell: row => {
        const status = row.status_publikasi;
        if (!status) {
          return <span className="badge" style={{ backgroundColor: '#6c757d', color: '#fff' }}>-</span>;
        }
        let badgeClass = 'status-sedang-verifikasi';
        if (status === 'Sudah Publikasi') {
          badgeClass = 'status-sudah-terintegrasi';
        } else if (status === 'Siap Publikasi') {
          badgeClass = 'status-perlu-perbaikan';
        }
        return (
          <span className={`badge ${badgeClass}`}>
            {status}
          </span>
        );
      }
    },
    {
      name: 'Verifikator',
      selector: row => row.verifikator || '-',
      sortable: true,
      minWidth: '100px',
      cell: row => (
        <div data-tooltip-id="verifikator-tooltip" data-tooltip-content={row.verifikator || '-'}>
          {row.verifikator && row.verifikator.length > 20 
            ? `${row.verifikator.substring(0, 20)}...` 
            : row.verifikator || '-'}
        </div>
      )
    },
    {
      name: 'Update',
      selector: row => row.last_modified ? new Date(row.last_modified) : new Date(0),
      sortable: true,
      minWidth: '100px',
      cell: row => {
        const displayDate = row.last_modified 
          ? new Date(row.last_modified).toLocaleDateString('id-ID', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            }) 
          : '-';
        return <div>{displayDate}</div>;
      }
    },
    {
      name: 'Aksi',
      cell: row => {
        const statusPublikasi = row.status_publikasi || 'Belum Publikasi';
        const canPublish = statusPublikasi === 'Siap Publikasi';
        const isPublished = statusPublikasi === 'Sudah Publikasi';
        
        return (
          <div className="publikasi-action-cell">
            <Link 
              to={`/rekapdata/${btoa(row.no_tiket)}`} 
              className="publikasi-icon-btn publikasi-view-btn"
              data-tooltip-id="action-tooltip"
              data-tooltip-content="Lihat Detail"
            >
              <FaEye />
            </Link>

            {canPublish && (
              <button
                className="publikasi-icon-btn publikasi-publish-btn"
                onClick={() => handlePublishClick(row.no_tiket, false)}
                data-tooltip-id="action-tooltip"
                data-tooltip-content="Publikasi Data"
              >
                <FaCheckCircle />
              </button>
            )}
            
            {isPublished && (
              <button
                className="publikasi-icon-btn publikasi-unpublish-btn"
                onClick={() => handlePublishClick(row.no_tiket, true)}
                data-tooltip-id="action-tooltip"
                data-tooltip-content="Batalkan Publikasi"
              >
                <FaTimesCircle />
              </button>
            )}
          </div>
        );
      },
      minWidth: '120px',
    }
  ];

  const conditionalRowStyles = [
    {
      when: row => row.status_publikasi === 'Sudah Publikasi',
      style: {
        backgroundColor: '#f0fdf4',
        borderLeft: '4px solid #10b981',
      },
    },
    {
      when: row => row.status_publikasi === 'Siap Publikasi',
      style: {
        backgroundColor: '#fffbeb',
        borderLeft: '4px solid #f59e0b',
      },
    },
    {
      when: row => !row.status_publikasi || row.status_publikasi === 'Belum Publikasi',
      style: {
        backgroundColor: '#f3f4f6',
        borderLeft: '4px solid #6b7280',
      },
    },
  ];

  const menuItems = getPublikatorMenuItems();

  return (
    <DashboardLayout menuItems={menuItems} userRole="Publikator">
      <div className="publikasi-container">
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/publikator">Dashboard</a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Publikasi Data
            </li>
          </ol>
        </nav>

        <div className="publikasi-search-section">
          <div className="publikasi-search-row">
            <div className="publikasi-search-container">
              <input
                type="text"
                className="publikasi-search-input"
                placeholder="Cari No Tiket, Nama IGT, atau KL"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button 
                className="publikasi-search-button"
                onClick={handleSearch}
                disabled={searchLoading}
              >
                <i className="fas fa-search"></i> Cari
              </button>
              {searchText && (
                <button 
                  className="publikasi-clear-button"
                  onClick={handleClearSearch}
                  title="Hapus pencarian"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
              <button 
                className="publikasi-refresh-button"
                onClick={handleRefresh}
                disabled={loading}
                title="Refresh data"
              >
                <FaSyncAlt className={loading ? 'spin' : ''} />
              </button>
            </div>
            
            <div className="publikasi-filter-buttons">
              <button
                className={`publikasi-filter-btn ${activeFilter === null ? 'active' : ''}`}
                onClick={() => handleStatCardClick('all')}
              >
                Semua ({stats.totalData})
              </button>
              <button
                className={`publikasi-filter-btn ${activeFilter === 'belum' ? 'active' : ''}`}
                onClick={() => handleStatCardClick('belum')}
              >
                Belum ({stats.dataBelumPublikasi})
              </button>
              <button
                className={`publikasi-filter-btn ${activeFilter === 'siap' ? 'active' : ''}`}
                onClick={() => handleStatCardClick('siap')}
              >
                Siap ({stats.dataSiapPublikasi})
              </button>
              <button
                className={`publikasi-filter-btn ${activeFilter === 'sudah' ? 'active' : ''}`}
                onClick={() => handleStatCardClick('sudah')}
              >
                Sudah ({stats.dataSudahPublikasi})
              </button>
            </div>
          </div>
        </div>

        <div className="publikasi-table-section" ref={tableRef}>
          <h3 className="publikasi-table-title">
            {filterTitle}
            <span className="publikasi-table-badge">{filteredData.length} Data</span>
          </h3>

          {error && <div className="publikasi-table-error">{error}</div>}
        
        <DataTable
          columns={columns}
          data={filteredData}
          progressPending={loading}
          progressComponent={<LoadingSpinner message="Memuat data..." type="spinner" size="medium" />}
          pagination
          highlightOnHover
          pointerOnHover
          defaultSortFieldId={1}
          defaultSortAsc={false}
          noDataComponent={<p className="text-center text-muted">Tidak ada data yang tersedia.</p>}
          responsive
          conditionalRowStyles={conditionalRowStyles}
        />
        </div>

        <Tooltip id="nama-tooltip" />
        <Tooltip id="kl-tooltip" />
        <Tooltip id="verifikator-tooltip" />
        <Tooltip id="action-tooltip" />

        {searchLoading && (
          <div className="loading-overlay-fullscreen">
            <LoadingSpinner message="Mencari data..." type="dots" size="large" />
          </div>
        )}

        <Modal
          isOpen={showPublishModal}
          onClose={() => setShowPublishModal(false)}
          type={publishAction === 'publish' ? 'confirm' : 'danger'}
          title={publishAction === 'publish' ? 'Konfirmasi Publikasi' : 'Konfirmasi Batalkan Publikasi'}
          message={`Apakah Anda yakin ingin ${publishAction === 'publish' ? 'mempublikasikan' : 'membatalkan publikasi'} data dengan tiket No ${selectedTicket}?`}
          onConfirm={handleConfirmPublish}
          confirmText={publishAction === 'publish' ? 'Ya, Publikasikan' : 'Ya, Batalkan'}
        />

        <Modal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          type="success"
          title="Berhasil"
          message={successMessage}
        />
      </div>
    </DashboardLayout>
  );
};

export default PublikatorDataPage;